import {
  useHasMoreMessages,
  useOldestMessages,
} from '../store/useMessageHooks';
import { useCallback } from 'react';
import { getDocs, limit, orderBy, query, startAfter } from 'firebase/firestore';
import { Message } from '../../type';
import { useMessageCollection } from '../../../../firebase/useCollection';
import { useUserFetcherForMessages } from './useUserFetcher';
import { useCareTeam } from '../store/useCareTeam';
import { useStorageInfoFetcher } from './useStorageInfoFetcher';
import { filterLast240Days } from './common';

export const useMessageLoader = (
  channelGuid: string,
): (() => Promise<void>) => {
  const [, dispatch] = useCareTeam();
  const collection = useMessageCollection(channelGuid);
  const enrichStorageInfo = useStorageInfoFetcher();
  const fetchUsers = useUserFetcherForMessages();
  const message = useOldestMessages(channelGuid);
  const currentHasMore = useHasMoreMessages(channelGuid);
  const pageSize = 40;
  return useCallback(async () => {
    if (!currentHasMore) {
      return;
    }
    const messageQuery = query(
      collection,
      orderBy('createdDate', 'desc'),
      startAfter(message?.createdDate.toDate() ?? new Date()),
      limit(pageSize),
    );
    const snapshot = await getDocs(messageQuery);
    const messages = snapshot.docs.map((doc) => doc.data() as Message);
    const messagesInLast240Days = filterLast240Days(messages);
    const storageInfoEnrichedMessages = await enrichStorageInfo(
      messagesInLast240Days,
    );
    const hasMore = messagesInLast240Days.length === pageSize;
    const users = await fetchUsers(messagesInLast240Days);
    dispatch({
      type: 'MESSAGE_LOADED',
      channelGuid,
      messages: storageInfoEnrichedMessages,
      hasMore,
      users,
    });
  }, [
    channelGuid,
    collection,
    message,
    fetchUsers,
    currentHasMore,
    dispatch,
    enrichStorageInfo,
  ]);
};
